import { useEffect, useState, useCallback } from 'react';
import { ImageCropper } from '../../shared/ui/ImageCropper';
import { cropImage } from '../../shared/utils/cropImage';

export const UploadPicture = ({
  onSelect,
  id = '',
  noPreview = false,
  initialValue,
  noLabel,
  accept = 'image/*',
  label = 'Загрузка изображения',
  textBtn = 'Загрузить файл',
  className = '',
  entityType = 'default',
}) => {
  const [mainImg, setMainImg] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);

  // Определение пропорций кропа в зависимости от типа сущности
  const getAspectRatio = useCallback(() => {
    switch (entityType) {
      case 'news':
        return 16 / 9; // Новости - широкоформатные изображения
      case 'product':
        return 1; // Продукты - квадратные изображения
      case 'event':
        return 16 / 9; // События - широкоформатные изображения
      case 'portfolio':
        return 16 / 9; // Портфолио - широкоформатные изображения
      case 'vacancy':
        return 3 / 2; // Вакансии
      case 'service':
        return 16 / 9; // Услуги - широкоформатные изображения
      case 'partner':
        return 3 / 2; // Партнеры
      case 'team':
        return 3 / 4; // Команда - вертикальные изображения
      default:
        return 16 / 9; // По умолчанию - широкоформатные изображения
    }
  }, [entityType]);

  useEffect(() => {
    if (initialValue) {
      const formattedUrl = initialValue.startsWith('http') 
        ? initialValue 
        : `${import.meta.env.VITE_API_URL}${initialValue}`;
      
      setMainImg({
        name: accept === 'image/*' ? '' : initialValue.replace('/uploads/', ''),
        value: formattedUrl,
      });
    }
  }, [initialValue, accept]);

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Сохраняем оригинальное имя файла
    const fileName = file.name || 'image.jpg';
    
    if (accept === 'image/*') {
      const imageUrl = URL.createObjectURL(file);
      setImageToEdit(imageUrl);
      
      // Сохраняем оригинальный файл с его именем
      const originalFileWithName = new File([file], fileName, { type: file.type });
      setOriginalFile(originalFileWithName);
      
      // Если нет необходимости в кропе, сразу отправляем файл
      if (noPreview) {
        setMainImg({
          name: fileName,
          value: imageUrl,
        });
        onSelect(originalFileWithName);
      } else {
        setShowCropper(true);
      }
    } else {
      // Для не-изображений
      const fileWithName = new File([file], fileName, { type: file.type });
      setMainImg({
        name: fileName,
        value: URL.createObjectURL(fileWithName),
      });
      onSelect(fileWithName);
    }
  };

  const handleCropComplete = async (cropResult) => {
    try {
      console.log('Полученный результат кропа:', cropResult);
      
      // Проверяем, что у нас есть изображение для кропа
      if (!imageToEdit) {
        console.error('Ошибка: изображение для кропа не определено');
        return;
      }

      // Проверяем формат результата кропа
      // Результат может быть либо объектом с параметрами кропа, либо объектом с blob
      if (cropResult.blob) {
        // Если получен blob напрямую из ImageCropper
        const fileName = originalFile?.name || 'cropped-image.jpg';
        const croppedFileWithName = new File([cropResult.blob], fileName, { 
          type: cropResult.blob.type || 'image/jpeg',
          lastModified: new Date().getTime()
        });

        console.log('Создан файл из blob:', {
          name: croppedFileWithName.name,
          size: croppedFileWithName.size,
          type: croppedFileWithName.type
        });

        setMainImg({
          name: fileName,
          value: cropResult.croppedImageUrl || URL.createObjectURL(croppedFileWithName),
        });

        onSelect(croppedFileWithName);
      } else {
        // Если получены параметры кропа, используем cropImage
        if (!cropResult || !cropResult.width || !cropResult.height) {
          console.error('Ошибка: некорректные параметры кропа', cropResult);
          return;
        }

        const croppedFile = await cropImage(imageToEdit, cropResult);

        // Проверяем, что файл был успешно создан
        if (!croppedFile) {
          console.error('Ошибка: не удалось создать файл после кропа');
          return;
        }

        // Создаем новый файл с сохранением имени оригинального файла
        const fileName = originalFile?.name || 'cropped-image.jpg';
        const croppedFileWithName = new File([croppedFile], fileName, { 
          type: croppedFile.type || 'image/jpeg',
          lastModified: new Date().getTime()
        });

        console.log('Обработанный файл:', {
          name: croppedFileWithName.name,
          size: croppedFileWithName.size,
          type: croppedFileWithName.type
        });

        setMainImg({
          name: fileName,
          value: URL.createObjectURL(croppedFileWithName),
        });

        onSelect(croppedFileWithName);
      }
    } catch (error) {
      console.error('Ошибка при кропе изображения:', error);
      // Если есть оригинальный файл, используем его как запасной вариант
      if (originalFile) {
        setMainImg({
          name: originalFile.name || '',
          value: URL.createObjectURL(originalFile),
        });
        onSelect(originalFile);
      }
    }
  };

  return (
    <div className={`mb-3 mb-flex ${className}`}>
      {!noLabel && (
        <label htmlFor="title" className="form-label">
          {label}
        </label>
      )}
      <div className="file-content d-flex align-items-center">
        <label className="at-btn at-btn-medium" htmlFor={`file-${accept}-${id}`}>
          {textBtn}
        </label>
        <input
          id={`file-${accept}-${id}`}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleChange}
          max={1}
        />
        <a
          href={initialValue 
            ? (initialValue.startsWith('http') ? initialValue : `${import.meta.env.VITE_API_URL}${initialValue}`) 
            : '#'}
          target={initialValue ? '_blank' : '_self'}
          className={'mb-0'}
        >
          {mainImg && mainImg.name}
        </a>
      </div>
      {!noPreview && accept === 'image/*' && (
        <div className={'d-flex align-items-center gap-2 mt-2'}>
          {mainImg &&
            (Array.isArray(mainImg) ? (
              mainImg.map((i) => (
                <img width={160} height={100} key={i} src={i} alt="Preview of image" />
              ))
            ) : (
              <img
                src={mainImg.value}
                className={'rounded-2 shadow-sm'}
                width={175}
                height={90}
                alt={`Preview of ${mainImg.name}`}
              />
            ))}
        </div>
      )}

      {/* Компонент кроппера изображений */}
      <ImageCropper
        image={imageToEdit}
        show={showCropper}
        onHide={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
        aspectRatio={getAspectRatio()}
      />
    </div>
  );
};
